"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from "react";
const RTL_PREFIXES = ["ar", "fa", "he", "ur", "ps", "sd", "yi"];
function detectDirection(locale) {
    const lang = locale.split("-")[0]?.toLowerCase() ?? "";
    return RTL_PREFIXES.some((p) => lang === p) ? "rtl" : "ltr";
}
function buildIntlBag(locale) {
    const currencyCache = new Map();
    return {
        numberFormat: new Intl.NumberFormat(locale),
        compactNumberFormat: new Intl.NumberFormat(locale, { notation: "compact" }),
        currencyFormat: (currency) => {
            const cached = currencyCache.get(currency);
            if (cached)
                return cached;
            const next = new Intl.NumberFormat(locale, { style: "currency", currency });
            currencyCache.set(currency, next);
            return next;
        },
        dateFormat: new Intl.DateTimeFormat(locale, { dateStyle: "medium" }),
        dateTimeFormat: new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }),
        relativeTimeFormat: new Intl.RelativeTimeFormat(locale, { numeric: "auto" }),
        listFormat: new Intl.ListFormat(locale, { style: "long", type: "conjunction" }),
    };
}
const I18nContext = createContext(null);
export function useI18n() {
    return useContext(I18nContext);
}
export function useFormatNumber() {
    const ctx = useI18n();
    return useCallback((value) => (ctx ? ctx.intl.numberFormat.format(value) : String(value)), [ctx]);
}
export function useFormatDate() {
    const ctx = useI18n();
    return useCallback((value) => ctx ? ctx.intl.dateFormat.format(value) : new Date(value).toISOString().slice(0, 10), [ctx]);
}
export function useFormatRelativeTime() {
    const ctx = useI18n();
    return useCallback((value, unit) => ctx ? ctx.intl.relativeTimeFormat.format(value, unit) : `${value} ${unit}`, [ctx]);
}
function interpolate(template, params = {}) {
    return template.replace(/\{(\w+)\}/g, (match, key) => {
        const value = params[key];
        return value === undefined ? match : String(value);
    });
}
export function I18nProvider({ children, defaultLocale, messages = {}, applyToDocument = true, }) {
    const initialLocale = defaultLocale ?? (typeof navigator !== "undefined" ? navigator.language : "en-US");
    const [locale, setLocaleState] = useState(initialLocale);
    const direction = detectDirection(locale);
    const intl = useMemo(() => buildIntlBag(locale), [locale]);
    const setLocale = useCallback((next) => {
        setLocaleState(next);
    }, []);
    useEffect(() => {
        if (!applyToDocument || typeof document === "undefined")
            return;
        document.documentElement.setAttribute("lang", locale);
        document.documentElement.setAttribute("dir", direction);
    }, [locale, direction, applyToDocument]);
    const t = useCallback((key, params) => {
        const template = messages[key];
        if (template === undefined)
            return key;
        return interpolate(template, params);
    }, [messages]);
    const value = useMemo(() => ({ locale, direction, setLocale, t, intl }), [locale, direction, setLocale, t, intl]);
    return _jsx(I18nContext.Provider, { value: value, children: children });
}
